import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useSystemId } from "../../../hooks/useReduxSelectors";
import { setYAxisForMeasurement, setYAxisForDiagnostics } from "../../../ducks/trendsDuck";
import { Box, Grid, ButtonGroup, Button, Accordion, AccordionDetails, TextField, Tooltip, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useTranslation } from "react-i18next";
import { useKeyboard } from "../../../hooks/KeyboardContext";

const labelSx = {
  fontSize: "0.75rem",
  fontWeight: 600,
  letterSpacing: "0.5px",
  textAlign: "center",
  m: 0,
  p: 0,
};

const YAxisButtons = ({ chartIndex }) => {
  const systemId = useSystemId();
  const { t } = useTranslation("common");
  const { registerInput } = useKeyboard();
  const dispatch = useDispatch();
  const [min, setMin] = useState("");
  const [max, setMax] = useState("");
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [inputSide, setInputSide] = useState("Left");
  const [showInputFields, setShowInputFields] = useState(false);

  const saveValues = useCallback(() => {
    const saveRequest = (data) => {
      const payload = {
        chart: chartIndex,
        systemId: systemId,
        data: data,
      };

      if (inputSide === "Left") {
        dispatch(setYAxisForMeasurement(payload));
        setMsg("Values for Measurements are set!");
      } else {
        dispatch(setYAxisForDiagnostics(payload));
        setMsg("Values for Diagnostics are set!");
      }
    };

    const data = inputValidationCheck(min, max);
    if (data !== "error") {
      setError("");
      saveRequest(data);
    }
  }, [min, max, inputSide, dispatch, chartIndex]);

  const resetValues = useCallback(() => {
    setMin("");
    setMax("");
    setError("");
    setMsg("");

    const payload = {
      chart: chartIndex,
      systemId: systemId,
      data: {},
    };

    if (inputSide === "Left") {
      dispatch(setYAxisForMeasurement(payload));
    } else {
      dispatch(setYAxisForDiagnostics(payload));
    }
  }, [dispatch, chartIndex, systemId, inputSide]);

  const onChange = useCallback((inputField, value) => {
    inputField === "min" ? setMin(value) : setMax(value);
  }, []);

  const onYAxisClicked = (side) => {
    if (showInputFields && inputSide !== side) {
      setInputSide(side);
    } else {
      setShowInputFields(!showInputFields);
      setInputSide(side);
    }
  };

  const inputValidationCheck = (min, max) => {
    try {
      let minWithDots = Number(min.toString().replace(/,/g, "."));
      let maxWithDots = Number(max.toString().replace(/,/g, "."));
      if (max === "" || min === "") {
        setMsg("");
        setError("Both Fields Are Required");
        return "error";
      } else if (maxWithDots <= minWithDots) {
        setMsg("");
        setError("Min value has to be smaller than max value");
        return "error";
      } else {
        let data = { start: minWithDots, end: maxWithDots };
        if (Object.values(data).some((v) => Number.isNaN(v))) {
          setMsg("");
          setError("Inputted values need to be numbers");
          return "error";
        }
        return data;
      }
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (min && max) {
      parseFloat(min) < parseFloat(max)
        ? setError("")
        : setError("Min value has to be smaller than max value") & setMsg("");
    } else {
      setError("Both Fields Are Required") & setMsg("");
    }
  }, [min, max]);

  useEffect(() => {
    setMin("");
    setMax("");
    setMsg("");
    setError("");
  }, [showInputFields]);

  const ToggleButton = ({ side, placement }) => (
    <Tooltip title="Set the Y-axis MIN & MAX values for measurements/diagnostics" placement={placement} enterDelay={350} leaveDelay={100}>
      <ButtonGroup sx={{ mb: "2px" }}>
        {side === "Right" && (
          <Button
            onClick={() => onYAxisClicked(side)}
            size="small"
            color={showInputFields && inputSide === side ? "secondary" : "primary"}
            sx={{ px: "3px", py: "5px", borderRight: "1px solid black" }}
          >
            {showInputFields && inputSide === side ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </Button>
        )}
        <Button
          onClick={() => onYAxisClicked(side)}
          size="small"
          color={showInputFields && inputSide === side ? "secondary" : "primary"}
        >
          Y
        </Button>
        {side === "Left" && (
          <Button
            onClick={() => onYAxisClicked(side)}
            size="small"
            color={showInputFields && inputSide === side ? "secondary" : "primary"}
            sx={{ px: "3px", py: "5px", borderLeft: "1px solid black" }}
          >
            {showInputFields && inputSide === side ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </Button>
        )}
      </ButtonGroup>
    </Tooltip>
  );

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", px: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Typography
            sx={{
              ...labelSx,
              color: showInputFields && inputSide === "Left" ? "secondary.main" : "text.primary",
            }}
          >
            {t("Measurements")}
          </Typography>
          <ToggleButton side="Left" placement="right" />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Typography
            sx={{
              ...labelSx,
              color: showInputFields && inputSide === "Right" ? "secondary.main" : "text.primary",
            }}
          >
            {t("Diagnostics")}
          </Typography>
          <ToggleButton side="Right" placement="left" />
        </Box>
      </Box>
      {showInputFields && (
        <Accordion defaultExpanded>
          <AccordionDetails>
            <form>
              <Grid container>
                <Grid size={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {inputSide === "Left" ? t("Measurements") : t("Diagnostics")}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container sx={{ justifyContent: "center", alignItems: "flex-end", gap: 2 }}>
                <Grid sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <Typography variant="body2" sx={{ p: 0, m: 0 }}>Min Value</Typography>
                  <TextField
                    value={min}
                    placeholder="Min ..."
                    onChange={(e) => onChange("min", e.target.value)}
                    onClick={() => registerInput("min", min, (v) => setMin(v))}
                    size="small"
                    sx={{ maxWidth: 200 }}
                  />
                </Grid>
                <Grid sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                  <Button variant="contained" color="success" size="small" onClick={() => saveValues()}>
                    Set Values
                  </Button>
                  <Button variant="contained" color="warning" size="small" onClick={() => resetValues()}>
                    Reset Values
                  </Button>
                </Grid>
                <Grid sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <Typography variant="body2" sx={{ p: 0, m: 0 }}>Max Value</Typography>
                  <TextField
                    value={max}
                    placeholder="Max ..."
                    onChange={(e) => onChange("max", e.target.value)}
                    onClick={() => registerInput("max", max, (v) => setMax(v))}
                    size="small"
                    sx={{ maxWidth: 200 }}
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid size={12} sx={{ display: "flex", justifyContent: "center" }}>
                  {error && <Typography sx={{ color: "error.main", fontSize: "0.85rem" }}>{error}</Typography>}
                  {msg && <Typography sx={{ color: "success.main", fontSize: "0.85rem" }}>{msg}</Typography>}
                </Grid>
              </Grid>
            </form>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default YAxisButtons;

